export const faqMap = [

  /* ═══════════════════════════════════════════════════════════
     GLOBAL TAGS — Core Identity & Products
  ═══════════════════════════════════════════════════════════ */

  {
    id: 'what-is-hekaos',
    group: 'global',
    label: 'What is HEKAOS',
    gradient: 'from-amber-400 to-orange-600',
    keywords: [
      'what is hekaos',
      'hekaos meaning',
      'what does hekaos do',
      'hekaos company',
      'about hekaos',
      'hekaos definition',
      'hekaos explained',
      'who is hekaos',
      'hekaos overview',
      'tell me about hekaos',
      'what is heka',
      'heka meaning',
      'hekaos pronunciation',
      'how to pronounce hekaos',
      '/ˈhē-kā-ˌōs/'
    ],
    question: 'What is HEKAOS?',
    answer: `HEKAOS is a system for engineering order from chaos—where data, models, and human intent converge into clarity and decisive action.

It's a modern philosophy of intelligence design: we blend ancient pattern-sense with machine reasoning to simulate futures, expose tradeoffs, and shape outcomes early.

An ecosystem of minds and machines that turns uncertainty into structured intelligence—and intelligence into durable value.

In practice: HEKAOS is not a single tool—it is an architecture for making intelligence intentional.`
  },

  {
    id: 'hekaos-vision',
    group: 'global',
    label: 'Vision',
    gradient: 'from-violet-400 to-purple-600',
    keywords: [
      'vision',
      'hekaos vision',
      'company vision',
      'future direction',
      'long term goal',
      'mission',
      'philosophy',
      'why hekaos',
      'strategic direction',
      'purpose',
      'hekaos mission',
      'company mission',
      'what drives hekaos',
      'hekaos purpose'
    ],
    question: 'What is the HEKAOS vision?',
    answer: `At HEKAOS, our vision is to build intelligence that reasons from first principles, adapts under pressure, and turns complexity into structured clarity.

We believe intelligence should be:
- Explainable — every insight traceable to source
- Adaptable — systems that evolve, not just optimize
- Intentional — designed with clear objectives, not emergent accidents

Our long-term vision: A unified layer where data, models, and human intent work together in real time—the Foresight Engine we've been building toward from day one.`
  },

  {
    id: 'transformation-process',
    group: 'global',
    label: 'Intelligence Process',
    gradient: 'from-cyan-400 to-blue-600',
    keywords: [
      'how hekaos works',
      'hekaos process',
      'transformation',
      'chaos to order',
      'intelligence transformation',
      'hekaos methodology',
      'how does it work',
      'intelligence cycle',
      'chaos order heka',
      'signal insight',
      'raw to engineered',
      'transformation cycle',
      'intelligence pipeline'
    ],
    question: 'How does the HEKAOS transformation process work?',
    answer: `The HEKAOS transformation follows a clear cycle:

**SIGNAL → HEKA → INSIGHT**

1. **SIGNAL (Raw)** — Unstructured data, ambiguity, and noise. The starting point where potential exists without structure.

2. **HEKA (Transform)** — Our intelligence engine processes raw signals through pattern recognition, knowledge graphs, and multi-agent reasoning to impose structure on entropy.

3. **INSIGHT (Order)** — Structured, actionable intelligence ready for confident decision-making.

This cycle applies to every engagement: from vague role ideas to precision-defined specifications, from scattered candidate data to optimized career positioning.`
  },

  {
    id: 'hekaos-platforms',
    group: 'global',
    label: 'Platforms',
    gradient: 'from-emerald-400 to-green-600',
    keywords: [
      'platforms',
      'hekaos platforms',
      'foundry noesis',
      'what platforms',
      'product platforms',
      'foundry vs noesis',
      'two platforms',
      'platform architecture',
      'foundry',
      'noesis',
      'hekaos foundry',
      'hekaos noesis',
      'platform difference'
    ],
    question: 'What are the HEKAOS platforms?',
    answer: `HEKAOS operates through two distinct but interconnected platforms:

**HEKAOS Foundry**
Where ideas are tested under heat, shaped under pressure, and hardened into products that operate in the real world. Every product forged from first principles—engineered to solve real problems.

**HEKAOS NOESIS**
The intelligence layer—SDKs, studio components, live intel feeds, and enterprise core infrastructure. Intelligence infrastructure for platforms that reason.

These aren't separate products—they're two sides of the same system: Foundry builds products, NOESIS provides the intelligence layer that powers them.`
  },

  {
    id: 'heka-atlas',
    group: 'global',
    label: 'HEKA Atlas',
    gradient: 'from-cyan-400 to-sky-600',
    keywords: [
      'atlas',
      'heka atlas',
      'hekaos atlas',
      'what is atlas',
      'atlas platform',
      'unified hiring',
      'hiring intelligence',
      'recruitment platform',
      'ats integration',
      'hiring orchestration',
      'talent platform',
      'recruitment intelligence',
      'hiring system',
      'atlas product'
    ],
    question: 'What is HEKA Atlas?',
    answer: `HEKA Atlas is the orchestration intelligence platform that unifies role requirements and candidate profiles into a single, bidirectional matching engine.

**Key Capabilities:**
- Unified Intelligence Layer — connects Codex (role blueprints) with Odyssey (candidate profiles)
- Cross-Platform Orchestration — automates end-to-end hiring workflows
- Real-Time Signal Exchange — syncs role and candidate signals continuously
- Enterprise Governance Hub — centralized compliance, audit trails, security
- Ecosystem Analytics — full-funnel intelligence from role definition to placement
- Closed-Loop Placement Intelligence — every hire feeds data back into the system

Built for: RPO providers, enterprise organizations, staffing platforms, and platform integrators who need both sides of the hire unified.`
  },

  {
    id: 'heka-codex',
    group: 'global',
    label: 'HEKA Codex',
    gradient: 'from-orange-400 to-amber-600',
    keywords: [
      'codex',
      'heka codex',
      'hekaos codex',
      'what is codex',
      'role intelligence',
      'job description',
      'jd generator',
      'role blueprinting',
      'hiring workflow',
      'interview framework',
      'role specification',
      'codex product',
      'role architecture',
      'jd creator',
      'resume parser'
    ],
    question: 'What is HEKA Codex?',
    answer: `HEKA Codex is the role intelligence workspace that transforms vague role ideas into precision-defined job architecture.

**Core Features:**
- Document Intelligence — generates JDs from blueprints, parses incoming JDs and resumes
- Candidate Intelligence — scores JD-to-candidate alignment across skills, experience, and fit
- Interview Intelligence — AI-powered mock interviews, competency-based question sets
- Workflow Automation — end-to-end hiring workflows from requisition to offer
- Hiring Governance — compliance enforcement, security clearances, audit trails
- Analytics & Enablement — hiring funnel insights, decision quality benchmarks

Built for: Recruiters, hiring managers, talent operations teams, recruitment agencies, and consulting firms who need structured, repeatable hiring intelligence.`
  },

  {
    id: 'heka-odyssey',
    group: 'global',
    label: 'HEKA Odyssey',
    gradient: 'from-violet-400 to-purple-600',
    keywords: [
      'odyssey',
      'heka odyssey',
      'hekaos odyssey',
      'what is odyssey',
      'career intelligence',
      'resume optimization',
      'job fit',
      'career path',
      'interview prep',
      'resume scoring',
      'career positioning',
      'odyssey product',
      'candidate intelligence',
      'resume analyzer',
      'career mapper'
    ],
    question: 'What is HEKA Odyssey?',
    answer: `HEKA Odyssey is the career intelligence platform that analyzes, optimizes, and positions your career—from first application to the right offer.

**Core Features:**
- Resume Intelligence — ATS compatibility audit, keyword gap analysis, impact clarity
- Job Fit Scoring — precise match scores with ranked gap analysis
- Career Path Intelligence — realistic progression routes with skill milestones
- Interview Readiness — role-specific questions, structured frameworks, mock interviews
- Narrative & Positioning — targeted cover letters, LinkedIn optimization
- Offer Decision Intelligence — compensation benchmarking, market analysis, trajectory scoring

Built for: Students, mid-level professionals, career switchers, and senior leaders who need data-backed career decisions, not guesswork.`
  },

  {
    id: 'noesis-sdk',
    group: 'global',
    label: 'NOESIS SDK',
    gradient: 'from-amber-400 to-yellow-600',
    keywords: [
      'noesis sdk',
      'sdk',
      'developer tools',
      'api',
      'intelligence sdk',
      'embed intelligence',
      'noesis developer',
      'integration',
      'developer toolkit',
      'hekaos sdk',
      'intelligence api',
      'noesis api',
      'developer platform'
    ],
    question: 'What is the NOESIS SDK?',
    answer: `The NOESIS SDK is a developer-first toolkit for embedding HEKAOS intelligence into any application.

APIs, modules, and agent frameworks built for teams who want to build on top of structured intelligence—not rebuild it from scratch.

**Features:**
- Pre-built intelligence modules
- Agent frameworks
- RESTful APIs
- Production-ready workflows
- Enterprise-grade security

Currently in closed access (v1.7). Contact our team for developer access.`
  },

  {
    id: 'noesis-studio',
    group: 'global',
    label: 'NOESIS Studio',
    gradient: 'from-violet-400 to-purple-600',
    keywords: [
      'noesis studio',
      'studio',
      'visual intelligence',
      'dashboards',
      'intelligence components',
      'data visualization',
      'studio components',
      'intelligence views',
      'hekaos studio',
      'visual components'
    ],
    question: 'What is NOESIS Studio?',
    answer: `NOESIS Studio provides visual intelligence components—dashboards, infographics, and data views built for enterprise teams and individuals who need insight without the engineering overhead.

Intelligence, made visible. Pre-built UI components that make complex intelligence accessible to non-technical users.`
  },

  {
    id: 'noesis-intel',
    group: 'global',
    label: 'NOESIS Intel',
    gradient: 'from-cyan-400 to-blue-600',
    keywords: [
      'noesis intel',
      'intel',
      'intelligence feed',
      'research',
      'pattern reports',
      'signal feed',
      'intelligence reports',
      'live intel',
      'hekaos intel',
      'decision intelligence'
    ],
    question: 'What is NOESIS Intel?',
    answer: `NOESIS Intel is a living feed of research, pattern reports, and decision intelligence—continuously updated, always traceable to source.

Signal over noise. A curated intelligence stream that separates meaningful patterns from market noise.`
  },

  {
    id: 'noesis-core',
    group: 'global',
    label: 'NOESIS Core',
    gradient: 'from-emerald-400 to-green-600',
    keywords: [
      'noesis core',
      'core',
      'enterprise infrastructure',
      'building blocks',
      'core infrastructure',
      'enterprise core',
      'foundation',
      'hekaos core',
      'platform core',
      'core engine'
    ],
    question: 'What is NOESIS Core?',
    answer: `NOESIS Core provides pre-built enterprise building blocks—workflows, pipelines, and agent frameworks production-ready from day one.

The foundation everything runs on. Core infrastructure that enterprises can deploy without building from scratch.`
  },

  /* ═══════════════════════════════════════════════════════════
     PHILOSOPHY & VISION
  ═══════════════════════════════════════════════════════════ */

  {
    id: 'philosophy',
    group: 'global',
    label: 'Philosophy',
    gradient: 'from-pink-400 to-rose-600',
    keywords: [
      'philosophy',
      'hekaos philosophy',
      'core beliefs',
      'principles',
      'values',
      'approach',
      'methodology',
      'thinking',
      'belief system',
      'axiom',
      'manifesto',
      'what we believe',
      'core values'
    ],
    question: 'What is the HEKAOS philosophy?',
    answer: `HEKAOS is built on outcome-based strategy where every system has an objective function. Ours is real-world impact—defined, tracked, and refined until the result is undeniable.

**Core Principles:**

**Objective First** — The architecture follows the goal, never the other way around. We define success before designing the system.

**Output Over Process** — What a system produces is the only metric that matters. We measure real-world results, not internal activity.

**Iterative Convergence** — We build, test, and close the gap between intended and actual outcomes—repeatedly, until the result holds.

**Adaptive Calibration** — When the environment shifts, the objective function evolves with it. Static strategy is a liability in dynamic systems.`
  },

  {
    id: 'three-pillars',
    group: 'global',
    label: 'Core Pillars',
    gradient: 'from-amber-400 to-orange-600',
    keywords: [
      'pillars',
      'three pillars',
      'core pillars',
      'foundation',
      'systems thinking',
      'engineered insight',
      'mythic architecture',
      'hekaos pillars',
      'foundational principles',
      'what drives hekaos'
    ],
    question: 'What are the three pillars of HEKAOS?',
    answer: `**Systems Thinking**
We model the whole—not just the immediate problem. Feedback loops, emergent behaviors, and second-order effects are native to our analytical framework. Understanding a system means understanding what it becomes under pressure.

**Engineered Insight**
Every recommendation is traceable. Every decision carries a clear line of reasoning. We reject opacity as a design choice—intelligence you cannot interrogate is intelligence you cannot trust.

**Mythic Architecture**
HEKAOS draws from Heka—the ancient Egyptian force through which structure emerges from chaos. We believe the oldest frameworks for understanding intelligence still hold. Our platform is built on that conviction: timeless principles, not passing trends.`
  },

  {
    id: 'manifesto',
    group: 'global',
    label: 'Manifesto',
    gradient: 'from-violet-400 to-purple-600',
    keywords: [
      'manifesto',
      'hekaos manifesto',
      'beliefs',
      'what we stand for',
      'core beliefs',
      'principles',
      'mission statement',
      'what drives us'
    ],
    question: 'What is the HEKAOS manifesto?',
    answer: `These are the beliefs that drive every product decision, every design choice, every line of code we write:

- Intelligence is structured understanding that survives contact with reality
- Chaos is unstructured potential—we engineer the signal from the noise
- Human judgment is the objective function—we sharpen it, never circumvent it
- Every insight must be explainable, traceable, and contestable
- Systems that adapt outlast systems that merely optimize
- At HEKAOS, how we think and what we build are the same act`
  },

  /* ═══════════════════════════════════════════════════════════
     PRODUCT DEEP DIVES — ATLAS
  ═══════════════════════════════════════════════════════════ */

  {
    id: 'atlas-who-for',
    group: 'global',
    label: 'Atlas Users',
    gradient: 'from-cyan-400 to-sky-600',
    keywords: [
      'who uses atlas',
      'atlas for who',
      'atlas users',
      'atlas customers',
      'who needs atlas',
      'atlas use cases',
      'rpo atlas',
      'enterprise hiring',
      'staffing platform',
      'talent platform atlas'
    ],
    question: 'Who is HEKA Atlas built for?',
    answer: `**RPO & Managed Service Providers**
Deliver end-to-end hiring intelligence to clients without building the infrastructure yourself. Manage role intelligence and candidate positioning from a single platform.

**Enterprise Organizations**
Unify hiring intelligence across business units, geographies, and seniority bands with centralized governance and closed-loop outcomes.

**Large Staffing & Talent Platforms**
Operate both sides of the talent marketplace with unified intelligence—match engine, automated workflow, placement outcome loop.

**Platform Integrators & Talent Tech Teams**
Embed Atlas intelligence into existing ATS, HRIS, and talent marketplace infrastructure via unified API.`
  },

  {
    id: 'atlas-capabilities',
    group: 'global',
    label: 'Atlas Features',
    gradient: 'from-emerald-400 to-green-600',
    keywords: [
      'atlas features',
      'atlas capabilities',
      'what does atlas do',
      'atlas functionality',
      'atlas platform features',
      'bidirectional matching',
      'hiring orchestration',
      'placement intelligence',
      'governance hub'
    ],
    question: 'What are the key capabilities of HEKA Atlas?',
    answer: `**Unified Intelligence Layer**
Connects role blueprints from Codex with candidate profiles from Odyssey into a single, bidirectional matching engine.

**Cross-Platform Orchestration**
Automate end-to-end hiring workflows that span both sides—from role requisition through offer acceptance—with zero manual handoffs.

**Real-Time Signal Exchange**
Sync role requirement signals and candidate positioning signals in real time—so matches sharpen as both sides evolve.

**Enterprise Governance Hub**
Centralize compliance, audit trails, security clearances, and structured approval workflows across all teams, entities, and geographies.

**Ecosystem Analytics**
Full-funnel intelligence from role definition quality to candidate conversion rate—surfacing bottlenecks on both sides.

**Closed-Loop Placement Intelligence**
Every placement feeds structured outcome data back into both Codex and Odyssey—continuously retraining role blueprints and candidate positioning.`
  },

  {
    id: 'atlas-workflow',
    group: 'global',
    label: 'Atlas Workflow',
    gradient: 'from-pink-400 to-rose-600',
    keywords: [
      'atlas workflow',
      'how atlas works',
      'atlas process',
      'atlas pipeline',
      'hiring cycle',
      'placement cycle',
      'atlas flow',
      'ingest align orchestrate'
    ],
    question: 'How does the HEKA Atlas workflow work?',
    answer: `**INGEST**
Codex defines the role blueprint. Odyssey positions the candidate. Atlas ingests both sides simultaneously.

**ALIGN**
Runs bidirectional gap analysis—aligning role requirements to candidate signals and surfacing precision match scores in real time.

**ORCHESTRATE**
Automated workflows coordinate screening, structured interviews, and evaluation rubrics across all stakeholders—with zero manual handoffs.

**CLOSE**
Placement intelligence closes the loop—capturing outcome data that refines both role blueprints and candidate positioning for the next cycle.`
  },

  {
    id: 'atlas-integration',
    group: 'global',
    label: 'Atlas Integration',
    gradient: 'from-amber-400 to-orange-600',
    keywords: [
      'atlas integration',
      'ats integration',
      'hris integration',
      'atlas api',
      'integrate atlas',
      'atlas white label',
      'embed atlas',
      'atlas platform integration'
    ],
    question: 'Can HEKA Atlas integrate with existing systems?',
    answer: `Yes. Atlas is designed for platform integrators and talent tech teams to embed intelligence into existing ATS, HRIS, and talent marketplace infrastructure.

**Integration Features:**
- ATS Integration compatible
- HRIS Compatible
- API-First Architecture
- White-Label Ready
- Unified API layer connects role signals, candidate data, and outcome intelligence into your current stack—without rebuilding it

Contact our team to discuss integration requirements and deployment options.`
  },

  /* ═══════════════════════════════════════════════════════════
     PRODUCT DEEP DIVES — CODEX
  ═══════════════════════════════════════════════════════════ */

  {
    id: 'codex-who-for',
    group: 'global',
    label: 'Codex Users',
    gradient: 'from-orange-400 to-amber-600',
    keywords: [
      'who uses codex',
      'codex for who',
      'codex users',
      'codex customers',
      'who needs codex',
      'codex use cases',
      'recruiter tool',
      'hiring manager',
      'recruitment agency'
    ],
    question: 'Who is HEKA Codex built for?',
    answer: `**Growth-Stage & Scale-Ups**
Build repeatable hiring infrastructure before inconsistency becomes a liability. Structure your first 10 hires the same way you'd structure your 100th.

**Enterprise Organizations**
Standardize hiring intelligence across business units, geographies, and seniority bands with compliance, governance, and audit readiness built in.

**Recruitment & Staffing Agencies**
Process more mandates with greater precision without scaling headcount. Turn every client brief into a structured, matchable role definition in minutes.

**Consulting & Professional Services**
Define, post, and evaluate roles rapidly as mandates shift—with audit-ready documentation and structured evaluation at every step.`
  },

  {
    id: 'codex-capabilities',
    group: 'global',
    label: 'Codex Features',
    gradient: 'from-violet-400 to-purple-600',
    keywords: [
      'codex features',
      'codex capabilities',
      'what does codex do',
      'codex functionality',
      'jd generation',
      'resume parsing',
      'interview framework',
      'bias detection',
      'role blueprinting'
    ],
    question: 'What are the key capabilities of HEKA Codex?',
    answer: `**Document Intelligence**
Generate precise job descriptions from role blueprints, then parse incoming JDs and resumes into structured, comparable signals.

**Candidate Intelligence**
Score JD-to-candidate alignment across skills, experience depth, and role fit—then screen at scale with AI-driven shortlisting.

**Interview Intelligence**
Conduct AI-powered mock interviews, auto-generate competency-based question sets, and equip every interviewer with structured scoring rubrics.

**Workflow Automation**
Automate end-to-end hiring workflows from requisition to offer with structured approval chains and role-based permissions.

**Hiring Governance**
Enforce compliance at every stage—manage security clearances, maintain audit-ready approval trails, standardize hiring controls.

**Analytics & Enablement**
Surface hiring funnel insights, benchmark decision quality, and continuously upskill hiring teams.`
  },

  {
    id: 'codex-workflow',
    group: 'global',
    label: 'Codex Workflow',
    gradient: 'from-cyan-400 to-sky-600',
    keywords: [
      'codex workflow',
      'how codex works',
      'codex process',
      'codex pipeline',
      'role definition',
      'jd creation',
      'candidate matching',
      'define structure match'
    ],
    question: 'How does the HEKA Codex workflow work?',
    answer: `**DEFINE**
Capture role intent, seniority, team context, and required competencies into a structured role definition layer.

**STRUCTURE**
AI assembles a complete role blueprint—JD copy, skill taxonomy, and evaluation framework—from your inputs.

**MATCH**
Parse incoming resumes and score each candidate against the role blueprint using objective, skill-based criteria.

**DECIDE**
Surface ranked shortlists, skill gap reports, and interview guides—ready for confident, data-backed hiring decisions.`
  },

  {
    id: 'codex-jd-generator',
    group: 'global',
    label: 'JD Generator',
    gradient: 'from-emerald-400 to-green-600',
    keywords: [
      'jd generator',
      'job description generator',
      'create jd',
      'ai jd',
      'jd creator',
      'generate job description',
      'jd automation',
      'role description',
      'jd template',
      'automated jd'
    ],
    question: 'How does the Codex JD generator work?',
    answer: `Codex's JD Creator transforms role blueprints into precise, structured job descriptions automatically.

**Process:**
1. Input role requirements, competencies, and seniority signals
2. AI generates complete JD copy with skill taxonomy
3. Structured output ready for posting or further refinement

Every generated JD is:
- ATS-optimized
- Competency-mapped
- Bias-checked
- Aligned to your role blueprint

This ensures consistency across all roles while eliminating the "rewrite from scratch" problem.`
  },

  {
    id: 'codex-resume-parser',
    group: 'global',
    label: 'Resume Parser',
    gradient: 'from-pink-400 to-rose-600',
    keywords: [
      'resume parser',
      'parse resume',
      'cv parser',
      'resume parsing',
      'extract resume data',
      'resume analyzer',
      'cv analysis',
      'resume extraction',
      'candidate data extraction'
    ],
    question: 'How does Codex parse resumes?',
    answer: `Codex's Resume Parser extracts structured data from incoming resumes and converts them into comparable signals.

**What it does:**
- Extracts skills, experience, education, certifications
- Normalizes data across different resume formats
- Maps candidate signals to role blueprint requirements
- Generates objective match scores
- Identifies skill gaps automatically

This eliminates manual resume screening and ensures every candidate is evaluated against the same objective criteria.`
  },

  {
    id: 'codex-interview-framework',
    group: 'global',
    label: 'Interview Framework',
    gradient: 'from-amber-400 to-orange-600',
    keywords: [
      'interview framework',
      'interview questions',
      'competency questions',
      'structured interview',
      'interview rubric',
      'ai interview',
      'mock interview',
      'interview prep',
      'interview scoring'
    ],
    question: 'What interview capabilities does Codex provide?',
    answer: `Codex provides comprehensive interview intelligence:

**AI Mock Interviews**
Conduct practice interviews with AI-powered simulation and feedback.

**Competency-Based Question Sets**
Auto-generate role-specific questions mapped to required competencies.

**Structured Scoring Rubrics**
Equip every interviewer with objective evaluation criteria calibrated to the role.

**Bias Detection**
Identify and flag potential bias risks in evaluation questions and scoring.

This ensures every candidate is assessed fairly against the same structured framework, improving hiring quality and defensibility.`
  },

  /* ═══════════════════════════════════════════════════════════
     PRODUCT DEEP DIVES — ODYSSEY
  ═══════════════════════════════════════════════════════════ */

  {
    id: 'odyssey-who-for',
    group: 'global',
    label: 'Odyssey Users',
    gradient: 'from-violet-400 to-purple-600',
    keywords: [
      'who uses odyssey',
      'odyssey for who',
      'odyssey users',
      'odyssey customers',
      'who needs odyssey',
      'odyssey use cases',
      'job seekers',
      'career changers',
      'students',
      'professionals'
    ],
    question: 'Who is HEKA Odyssey built for?',
    answer: `**Students & Graduates**
Enter the market positioned to compete—not just to apply. Translate academic experience into role-ready language that clears ATS filters.

**Mid-Level Professionals**
You have the depth—the problem is articulating it. Odyssey surfaces your real impact and positions you for the role, not just the title.

**Career Switchers**
Your experience isn't irrelevant—it's mis-framed. Odyssey maps your transferable skills to your target domain and positions your pivot as deliberate and strategic.

**Senior Leaders**
At this level, positioning is everything. Command the room before you're in it with executive-grade narrative and precise compensation intelligence.`
  },

  {
    id: 'odyssey-capabilities',
    group: 'global',
    label: 'Odyssey Features',
    gradient: 'from-cyan-400 to-sky-600',
    keywords: [
      'odyssey features',
      'odyssey capabilities',
      'what does odyssey do',
      'odyssey functionality',
      'resume optimization',
      'job fit scoring',
      'career mapping',
      'interview preparation',
      'offer analysis'
    ],
    question: 'What are the key capabilities of HEKA Odyssey?',
    answer: `**Resume Intelligence**
Audit your resume for ATS compatibility, keyword gaps, and impact clarity—then receive a structured rewrite roadmap.

**Job Fit Scoring**
Decode any job description against your profile—get precise match scores and ranked gap analysis before you apply.

**Career Path Intelligence**
Map realistic progression routes from where you are to where you want to be—with skill milestones and transition timelines.

**Interview Readiness**
Predict role-specific interview questions, build structured story-driven answers, and rehearse with AI-powered mock interviews.

**Narrative & Positioning**
Generate targeted cover letters, LinkedIn positioning, and professional summaries—calibrated to role, company tone, and your career story.

**Offer Decision Intelligence**
Evaluate every compensation package against live market benchmarks, role growth signals, and your long-term trajectory.`
  },

  {
    id: 'odyssey-workflow',
    group: 'global',
    label: 'Odyssey Workflow',
    gradient: 'from-emerald-400 to-green-600',
    keywords: [
      'odyssey workflow',
      'how odyssey works',
      'odyssey process',
      'odyssey pipeline',
      'career journey',
      'resume to offer',
      'assess enhance practice',
      'career intelligence cycle'
    ],
    question: 'How does the HEKA Odyssey workflow work?',
    answer: `**ASSESS**
Upload your resume and target role. Get structured gap analysis across skills, keywords, and positioning signals.

**ENHANCE**
AI rewrites and optimizes your resume, cover letter, and profile for maximum role relevance and ATS performance.

**PRACTICE**
Prepare with predicted interview questions, structured answer frameworks, and mock simulation feedback.

**DECIDE**
Evaluate offers with salary benchmarking, risk signals, and career trajectory analysis—move forward with confidence.`
  },

  {
    id: 'odyssey-resume-analyzer',
    group: 'global',
    label: 'Resume Analyzer',
    gradient: 'from-pink-400 to-rose-600',
    keywords: [
      'resume analyzer',
      'resume audit',
      'ats check',
      'resume scoring',
      'resume optimization',
      'resume feedback',
      'cv analyzer',
      'resume review',
      'ats compatibility'
    ],
    question: 'How does Odyssey analyze resumes?',
    answer: `Odyssey's Resume Analyzer performs comprehensive audits:

**ATS Compatibility Check**
Identifies formatting issues, keyword gaps, and parsing problems that cause ATS rejections.

**Impact Clarity Audit**
Evaluates whether your achievements are quantified and clearly communicated.

**Keyword Gap Analysis**
Compares your resume against target role requirements and identifies missing signals.

**Structured Rewrite Roadmap**
Provides specific, actionable recommendations for optimization—not generic advice.

The result: A resume positioned to pass ATS filters AND impress human reviewers.`
  },

  {
    id: 'odyssey-job-fit',
    group: 'global',
    label: 'Job Fit Scoring',
    gradient: 'from-amber-400 to-orange-600',
    keywords: [
      'job fit',
      'fit score',
      'match score',
      'job matching',
      'role fit',
      'compatibility score',
      'jd decoder',
      'application fit',
      'match percentage'
    ],
    question: 'How does Odyssey calculate job fit scores?',
    answer: `Odyssey's Job Fit Scoring decodes any job description against your profile:

**What it analyzes:**
- Skills match (required vs. nice-to-have)
- Experience depth and relevance
- Seniority alignment
- Industry/domain fit
- Cultural/company fit signals

**What you get:**
- Precise match percentage (e.g., 84% fit)
- Ranked gap analysis showing what's missing
- Tailoring guidance for application materials
- Recommendation: apply, improve first, or skip

This ensures you only apply where you're positioned to win—saving time and improving success rates.`
  },

  {
    id: 'odyssey-career-path',
    group: 'global',
    label: 'Career Path Mapper',
    gradient: 'from-violet-400 to-purple-600',
    keywords: [
      'career path',
      'career mapping',
      'career progression',
      'career roadmap',
      'skill milestones',
      'career planning',
      'next role',
      'career trajectory',
      'progression route'
    ],
    question: 'How does Odyssey map career paths?',
    answer: `Odyssey's Career Path Mapper provides realistic progression routes:

**What it does:**
- Identifies viable next roles based on your current position
- Maps required skill acquisitions for each path
- Estimates realistic transition timelines
- Benchmarks market demand for target roles
- Highlights skill gaps to close

**Output:**
A clear roadmap showing:
- Where you are now
- Where you can realistically go
- What skills you need to build
- How long each transition typically takes
- Which path aligns best with your trajectory

This transforms vague career goals into structured, actionable plans.`
  },

  {
    id: 'odyssey-interview-prep',
    group: 'global',
    label: 'Interview Prep',
    gradient: 'from-cyan-400 to-sky-600',
    keywords: [
      'interview prep',
      'interview preparation',
      'mock interview',
      'interview practice',
      'interview questions',
      'star method',
      'interview simulator',
      'interview coaching',
      'behavioral questions'
    ],
    question: 'What interview preparation does Odyssey provide?',
    answer: `Odyssey's Interview Readiness system includes:

**Role-Predicted Questions**
AI generates likely interview questions based on the specific role and company.

**Structured Answer Frameworks**
Learn to build STAR-driven answers (Situation, Task, Action, Result) with templates and examples.

**Story Builder**
Organize your career experiences into compelling, reusable interview stories.

**Mock Interview Simulator**
Practice with AI-powered interviews that provide real-time feedback on your responses.

**Competency Mapping**
Understand which skills/competencies each question tests, so you can prepare targeted examples.

Walk into every interview with practiced, confident narratives—not improvised anxiety.`
  },

  {
    id: 'odyssey-offer-analysis',
    group: 'global',
    label: 'Offer Analysis',
    gradient: 'from-emerald-400 to-green-600',
    keywords: [
      'offer analysis',
      'offer evaluation',
      'compensation analysis',
      'salary benchmarking',
      'offer comparison',
      'offer decision',
      'salary negotiation',
      'compensation intelligence',
      'offer risk'
    ],
    question: 'How does Odyssey analyze job offers?',
    answer: `Odyssey's Offer Decision Intelligence evaluates every compensation package:

**What it analyzes:**
- Base salary vs. market benchmarks
- Total compensation (equity, bonuses, benefits)
- Role growth potential and career trajectory
- Company stability and market position
- Geographic/cost-of-living adjustments
- Risk signals (funding stage, market conditions)

**What you get:**
- Data-backed recommendation (accept, negotiate, decline)
- Specific negotiation points if applicable
- Long-term value projection
- Risk assessment
- Comparison against your career goals

No offer decision made on gut feel alone—every choice backed by market intelligence.`
  },

  /* ═══════════════════════════════════════════════════════════
     TECHNICAL & INTEGRATION
  ═══════════════════════════════════════════════════════════ */

  {
    id: 'api-access',
    group: 'global',
    label: 'API Access',
    gradient: 'from-cyan-400 to-sky-600',
    keywords: [
      'api',
      'api access',
      'developer access',
      'integration api',
      'hekaos api',
      'rest api',
      'api documentation',
      'api reference',
      'developer api'
    ],
    question: 'How do I access the HEKAOS API?',
    answer: `API access and comprehensive documentation are currently in development.

For API access, authentication details, rate limits, and custom integrations, contact our engineering team directly.

We work with partners to provide:
- RESTful API endpoints
- Authentication via API keys
- Webhooks for real-time updates
- SDKs for common languages
- Custom integration support

Reach out via WhatsApp or our contact form to discuss your integration needs.`
  },

  {
    id: 'data-security',
    group: 'global',
    label: 'Security',
    gradient: 'from-red-400 to-pink-600',
    keywords: [
      'security',
      'data security',
      'privacy',
      'data protection',
      'encryption',
      'compliance',
      'gdpr',
      'data privacy',
      'secure',
      'safe',
      'confidential'
    ],
    question: 'How does HEKAOS ensure data security?',
    answer: `Security is embedded into our architecture from day one:

- **Secure coding practices** — following industry-standard security protocols
- **Cloud security standards** — enterprise-grade infrastructure
- **Access control & encryption** — data encrypted in transit and at rest
- **Compliance-aware design** — built with GDPR and enterprise compliance in mind
- **Audit trails** — comprehensive logging for governance and compliance

We design for resilience and security from the foundation—not as an afterthought.

For specific compliance requirements or security audits, contact our team.`
  },

  {
    id: 'technology-stack',
    group: 'global',
    label: 'Technology',
    gradient: 'from-violet-400 to-purple-600',
    keywords: [
      'technology',
      'tech stack',
      'technologies',
      'tools',
      'frameworks',
      'programming languages',
      'infrastructure',
      'what technology',
      'built with',
      'tech used'
    ],
    question: 'What technologies does HEKAOS use?',
    answer: `We work across modern stacks including:

**Frontend:**
- React, Next.js
- TypeScript
- Tailwind CSS

**Backend:**
- Node.js, Python
- FastAPI
- AI/ML frameworks (PyTorch, TensorFlow)

**Infrastructure:**
- AWS, Azure, GCP
- DevOps & CI/CD systems
- Docker, Kubernetes

**Intelligence:**
- LLMs (Claude, GPT-4)
- Vector databases
- Knowledge graphs
- Multi-agent frameworks

Technology selection is driven by scalability, maintainability, and alignment with our intelligence architecture—not trends.`
  },

  {
    id: 'platform-uptime',
    group: 'global',
    label: 'Reliability',
    gradient: 'from-emerald-400 to-green-600',
    keywords: [
      'uptime',
      'reliability',
      'availability',
      'downtime',
      'system reliability',
      'platform uptime',
      'service level',
      'sla',
      'performance'
    ],
    question: 'What is the HEKAOS platform uptime?',
    answer: `We maintain 99.9% platform uptime with enterprise-grade infrastructure.

**Our approach:**
- Redundant systems and failover protocols
- 24/7 monitoring and alerting
- Automated scaling for load management
- Regular performance optimization
- Incident response protocols

For enterprise SLA requirements, contact our team to discuss custom service level agreements.`
  },

  /* ═══════════════════════════════════════════════════════════
     BUSINESS & ENGAGEMENT
  ═══════════════════════════════════════════════════════════ */

  {
    id: 'industries-served',
    group: 'global',
    label: 'Industries',
    gradient: 'from-amber-400 to-orange-600',
    keywords: [
      'industries',
      'sectors',
      'verticals',
      'who do you serve',
      'which industries',
      'target industries',
      'industry focus',
      'markets',
      'segments'
    ],
    question: 'Which industries does HEKAOS serve?',
    answer: `We work with clients across multiple sectors:

- **Technology & SaaS** — product companies, platforms
- **Finance & FinTech** — banking, payments, investment
- **Healthcare** — health tech, providers, pharma
- **Manufacturing** — operations, supply chain
- **E-commerce** — marketplaces, retail tech
- **Enterprise & Startups** — both growth-stage and established organizations

Our intelligence systems adapt to both growth-stage and enterprise environments across industries.`
  },

  {
    id: 'pricing-model',
    group: 'global',
    label: 'Pricing',
    gradient: 'from-cyan-400 to-sky-600',
    keywords: [
      'pricing',
      'cost',
      'how much',
      'price',
      'rates',
      'fees',
      'budget',
      'investment',
      'what does it cost',
      'pricing model',
      'subscription'
    ],
    question: 'How does HEKAOS pricing work?',
    answer: `Pricing depends on scope, complexity, and engagement model.

**We offer:**
- Fixed-price projects
- Dedicated teams
- Retainer models
- Strategic advisory packages
- Custom enterprise agreements

**Factors affecting pricing:**
- Platform/product selection
- Integration complexity
- User volume
- Support level
- Customization requirements

Contact us for a tailored estimate based on your specific needs. We'll work with you to structure an engagement that aligns with your objectives and budget.`
  },

  {
    id: 'getting-started',
    group: 'global',
    label: 'Get Started',
    gradient: 'from-violet-400 to-purple-600',
    keywords: [
      'get started',
      'how to start',
      'begin',
      'onboarding',
      'first steps',
      'start using',
      'sign up',
      'how to begin',
      'getting started',
      'start process'
    ],
    question: 'How do I get started with HEKAOS?',
    answer: `**Step 1: Initial Contact**
Reach out via WhatsApp (+91 92353 27048) or our contact form. Tell us what you're building or what problem you're solving.

**Step 2: Discovery Call**
We'll schedule a conversation to understand your needs, discuss which HEKAOS products fit, and outline potential approaches.

**Step 3: Proposal**
We'll provide a structured proposal with scope, timeline, deliverables, and investment.

**Step 4: Kickoff**
Once aligned, we begin with system mapping, architecture design, and structured execution.

**Timeline:**
Initial response within 24 hours. Discovery call typically within 2-3 business days.`
  },

  {
    id: 'support-model',
    group: 'global',
    label: 'Support',
    gradient: 'from-emerald-400 to-green-600',
    keywords: [
      'support',
      'customer support',
      'help',
      'assistance',
      'maintenance',
      'post launch',
      'ongoing support',
      'technical support',
      'help desk'
    ],
    question: 'What kind of support does HEKAOS provide?',
    answer: `We offer comprehensive post-launch support:

**Ongoing Maintenance**
Bug fixes, performance optimization, system updates

**Feature Enhancements**
Continuous improvement and new capability development

**Monitoring & Optimization**
Proactive system monitoring and performance tuning

**SLA-Based Enterprise Support**
Custom service level agreements for enterprise clients with dedicated support channels

**Support Channels:**
- WhatsApp for direct communication
- Email support
- Scheduled check-ins (for retainer clients)
- Emergency escalation protocols (enterprise)

We don't hand off and disappear—we build long-term partnerships.`
  },

  {
    id: 'development-timeline',
    group: 'global',
    label: 'Timeline',
    gradient: 'from-pink-400 to-rose-600',
    keywords: [
      'timeline',
      'how long',
      'duration',
      'time to build',
      'development time',
      'project length',
      'delivery time',
      'turnaround',
      'how fast'
    ],
    question: 'How long does HEKAOS development take?',
    answer: `Project timelines vary based on complexity:

**Typical Ranges:**
- Simple integrations: 2-4 weeks
- Product implementations: 6-12 weeks
- Custom platforms: 3-6 months
- Enterprise systems: 6-12 months

**Our Process:**
1. Discovery & System Mapping (1-2 weeks)
2. Architecture & Design (1-2 weeks)
3. Agile Development (iterative sprints)
4. QA & Performance Testing (ongoing)
5. Deployment & Optimization (1-2 weeks)

We emphasize structured execution and measurable outcomes over arbitrary speed.

Exact timelines provided in project proposals after discovery.`
  },

  {
    id: 'remote-work',
    group: 'global',
    label: 'Remote Work',
    gradient: 'from-amber-400 to-orange-600',
    keywords: [
      'remote',
      'distributed',
      'location',
      'work remotely',
      'remote team',
      'international',
      'global',
      'time zones',
      'where are you'
    ],
    question: 'Does HEKAOS work with remote or international clients?',
    answer: `Yes. We operate with global clients and distributed teams.

**How we work remotely:**
- Structured communication cadence (daily standups, weekly reviews)
- Time zone alignment for key meetings
- Async-first communication for efficiency
- Comprehensive documentation
- Real-time collaboration tools
- Regular progress reporting

**Headquarters:** India (UTC+5:30)

**We work with clients in:** North America, Europe, Asia-Pacific, Middle East

We align time zones, communication protocols, and structured reporting to ensure seamless execution regardless of location.`
  },

  /* ═══════════════════════════════════════════════════════════
     LOCAL TAGS — Specific Operations
  ═══════════════════════════════════════════════════════════ */

  {
    id: 'careers',
    group: 'local',
    label: 'Careers',
    gradient: 'from-violet-400 to-purple-600',
    keywords: [
      'careers',
      'jobs',
      'hiring',
      'work at hekaos',
      'join team',
      'openings',
      'positions',
      'employment',
      'job opportunities',
      'recruiting',
      'talent',
      'apply'
    ],
    question: 'Is HEKAOS hiring?',
    answer: `We are always looking for exceptional talent in engineering, AI, and systems design.

**Who we hire:**
- AI Engineers (ML/DL, NLP, Computer Vision)
- Full-Stack Engineers (React, Node.js, Python)
- Product Designers (UI/UX, Design Systems)
- Data Scientists (Statistical Modeling, Analytics)

**Our values:**
- Intelligence First
- Ship & Learn
- Outcome Driven
- Radical Collaboration

**Application requires:**
- Valid referral code
- Resume/CV
- Cover letter or portfolio

Visit our Careers page or contact us via WhatsApp for current openings and referral codes.

We don't hire for roles—we hire for thinking.`
  },

  {
    id: 'referral-code',
    group: 'local',
    label: 'Referral Code',
    gradient: 'from-cyan-400 to-sky-600',
    keywords: [
      'referral',
      'referral code',
      'access code',
      'application code',
      'how to apply',
      'need code',
      'get referral',
      'reference code'
    ],
    question: 'How do I get a referral code to apply?',
    answer: `Referral codes are required for all career applications to ensure quality and intentionality.

**How to get a referral code:**

1. **Network Referral:** If you know someone at HEKAOS, ask them for a referral code
2. **Direct Request:** Contact us via WhatsApp (+91 92353 27048) explaining your background and interest
3. **Community:** Engage with our content, contribute to discussions, demonstrate alignment with our philosophy

We provide referral codes to candidates who demonstrate:
- Alignment with our values
- Relevant skills and experience
- Genuine interest in intelligence engineering
- Clear career intent

Contact us to request a referral code—we'll review and respond within 48 hours.`
  },

  {
    id: 'office-location',
    group: 'local',
    label: 'Location',
    gradient: 'from-emerald-400 to-green-600',
    keywords: [
      'location',
      'office',
      'where are you',
      'address',
      'headquarters',
      'office address',
      'find you',
      'visit',
      'based in',
      'india'
    ],
    question: 'Where is HEKAOS located?',
    answer: `**Headquarters:** India

**Operating Model:** Fully remote, async-first, globally distributed

While headquartered in India, we operate as a distributed team with clients and collaborators worldwide.

**Office Hours:**
- Monday – Friday: 9:00 AM – 7:00 PM IST
- Saturday: 10:00 AM – 4:00 PM IST
- Sunday: Closed

View our location on [Google Maps](https://maps.app.goo.gl/XkM9JFPF4DeMtoSf8).`
  },

  {
    id: 'contact-methods',
    group: 'local',
    label: 'Contact',
    gradient: 'from-pink-400 to-rose-600',
    keywords: [
      'contact',
      'reach you',
      'get in touch',
      'email',
      'phone',
      'whatsapp',
      'message',
      'talk to you',
      'connect',
      'reach out'
    ],
    question: 'How can I contact HEKAOS?',
    answer: `**WhatsApp (Preferred):**
+91 92353 27048
[Message us directly](https://wa.me/919235327048)

**Email:**
hekaosIT@gmail.com

**Response Time:**
Typically within 24 hours (Mon-Sat)

**Office Hours:**
- Monday – Friday: 9:00 AM – 7:00 PM IST
- Saturday: 10:00 AM – 4:00 PM IST
- Sunday: Closed

**Contact Form:**
Available on our Connect page for detailed inquiries

**Social Media:**
- LinkedIn: [/company/hekaos](https://linkedin.com/company/hekaos)
- Twitter/X: [@hekaosinc](https://twitter.com/hekaosinc)
- Instagram: [@hekaos](https://instagram.com/hekaos)

For urgent matters, WhatsApp is the fastest channel.`
  },

  {
    id: 'business-hours',
    group: 'local',
    label: 'Hours',
    gradient: 'from-amber-400 to-orange-600',
    keywords: [
      'hours',
      'business hours',
      'open',
      'when open',
      'operating hours',
      'availability',
      'schedule',
      'timing',
      'office hours'
    ],
    question: 'What are HEKAOS business hours?',
    answer: `**Weekdays:**
Monday – Friday: 9:00 AM – 7:00 PM IST

**Weekend:**
Saturday: 10:00 AM – 4:00 PM IST
Sunday: Closed

**Time Zone:** India Standard Time (UTC+5:30)

**Note:** While these are our standard hours, as a distributed team we often accommodate different time zones for client meetings and support.

For urgent matters outside business hours, contact via WhatsApp—we monitor messages regularly.`
  },

  {
    id: 'blog-articles',
    group: 'local',
    label: 'Articles',
    gradient: 'from-violet-400 to-purple-600',
    keywords: [
      'blog',
      'articles',
      'content',
      'writing',
      'posts',
      'research',
      'publications',
      'insights',
      'thought leadership',
      'medium',
      'linkedin posts'
    ],
    question: 'Where can I read HEKAOS articles and research?',
    answer: `We publish technical deep-dives and intelligence insights across multiple platforms:

**LinkedIn:**
Regular posts on intelligence engineering, systems thinking, and product philosophy

**Instagram:**
Visual content and design thinking

**Medium:**
Long-form technical articles and case studies

**Twitter/X:**
Real-time thoughts, industry commentary

**Our Blog:**
Visit the Articles section on hekaos.com for curated content

**Topics we cover:**
- Intelligence system architecture
- AI engineering patterns
- Systems thinking frameworks
- Product philosophy
- Technical deep-dives
- Industry analysis

Filter by platform or topic in our Articles section.`
  },

  {
    id: 'documentation',
    group: 'local',
    label: 'Documentation',
    gradient: 'from-cyan-400 to-sky-600',
    keywords: [
      'documentation',
      'docs',
      'guides',
      'tutorials',
      'manual',
      'how to',
      'reference',
      'developer docs',
      'user guide'
    ],
    question: 'Where is the HEKAOS documentation?',
    answer: `Comprehensive documentation is currently in development.

**Coming Soon:**
- Getting Started guides
- Integration tutorials
- API reference
- Best practices
- Code examples
- Video walkthroughs

**Current Access:**
For immediate documentation needs, contact our team directly. We can provide:
- Setup guides
- Integration walkthroughs
- Technical specifications
- Custom onboarding

Documentation will be available at docs.hekaos.com once launched.

**Need help now?**
Contact us via WhatsApp—our team can walk you through any setup or integration questions.`
  },

  {
    id: 'partnerships',
    group: 'local',
    label: 'Partnerships',
    gradient: 'from-emerald-400 to-green-600',
    keywords: [
      'partnership',
      'partner',
      'collaborate',
      'collaboration',
      'work together',
      'strategic partner',
      'integration partner',
      'reseller',
      'affiliate'
    ],
    question: 'Does HEKAOS offer partnership opportunities?',
    answer: `Yes. We're open to strategic partnerships that align with our mission.

**Partnership Types:**

**Technology Partners**
Integration with complementary platforms and tools

**Consulting Partners**
Agencies and consultancies who implement HEKAOS for their clients

**Research Partners**
Academic institutions or research organizations

**Strategic Partners**
Long-term collaborations on product development or market expansion

**What we look for:**
- Alignment with our philosophy and values
- Complementary capabilities
- Commitment to quality and structured execution
- Win-win value creation

**Contact us to discuss:**
- Your organization's focus
- How our capabilities complement yours
- Potential collaboration models
- Mutual value proposition

Reach out via WhatsApp or email to explore partnership opportunities.`
  }

];